import React, { useEffect, useMemo, useRef, useState } from 'react';
import { get, isEqual, set, cloneDeep } from 'lodash';
import { useMemoizedFn, useDebounceFn } from 'ahooks';
import { v4 as uuid } from 'uuid';
import { ProTablePlus } from '@bedrock/eed-react';

export type ProTablePlusFormColumn = React.ComponentProps<
  typeof ProTablePlus
>['columns'][number];

const isEqualByKeys = (a: any, b: any, mergeKeys: (string[] | string)[]) => {
  return mergeKeys.every((keys) => {
    return isEqual(get(a, keys), get(b, keys));
  });
};

const AddRowKeys = ['$addRow', '$addRowParentId'];

// 深度合并
const deepMerge = (target, source) => {
  Object.keys(source).forEach((key) => {
    if (
      source[key] &&
      typeof source[key] === 'object' &&
      !Array.isArray(source[key])
    ) {
      if (!target[key]) target[key] = {};
      deepMerge(target[key], source[key]);
    } else {
      target[key] = source[key];
    }
  });
};

// 合并行，基于mergeKeys
export function mergeRowSpanByKeys(
  mergeKeys: (string[] | string)[],
  index: number,
  data: any[]
) {
  let rowSpan = 1;
  // 如果不是第一行，则判断是否与上一行相同，如果相同则返回0
  if (index >= 0 && isEqualByKeys(data[index], data[index - 1], mergeKeys)) {
    return 0;
  }
  // 和下一行比较，如果相同则+1，同时继续对比下一个，直到不相同为止
  for (let i = index + 1; i < data.length; i++) {
    if (isEqualByKeys(data[index], data[i], mergeKeys)) {
      rowSpan++;
    } else {
      break;
    }
  }
  return rowSpan;
}

export type AddGroupFormColumn = ProTablePlusFormColumn & {
  title?:
    | null
    | string
    | React.ReactNode
    | ((r: any, i: number) => React.ReactNode);
  addGroup?: boolean;
  addCellRender?: (r: any, i: number) => React.ReactNode;
  mergeRowSpanKey?: string[];
  children?: AddGroupFormColumn[];
  addGroupKey?: string[];
  formItem?: any; // 针对SForm的表单项
  treeChangeReady?: boolean; // 是否已经劫持了树形编辑的onChange
};

// 合并onCell
function mergeOnCell(onCells: ProTablePlusFormColumn['onCell'][]) {
  return (record: any, index: number) => {
    return onCells.reduce((acc, onCell = {}) => {
      return {
        ...acc,
        ...(typeof onCell === 'function' ? onCell(record, index) : onCell),
      };
    }, {});
  };
}

// 树结构数据
const getTreeData = (data: any[], columns: AddGroupFormColumn[]) => {
  let needAddCellList: {
    parentKey: string;
    needAddCell: boolean;
    addRowKeys: string[];
  }[] = [];
  // 基于columns的addGroup结构生成一个树结构
  let treeModel: {
    nodeIdKey: string;
    level: number;
    dataIndex: string | string[];
    parentKey: string;
  }[] = [];
  function initTreeModel(columns, parentKey, level: number) {
    columns.forEach((column) => {
      if (column.addGroup) {
        const parentKey =
          typeof column.addGroupKey === 'string'
            ? column.addGroupKey
            : column.addGroupKey[column.addGroupKey.length - 1];
        needAddCellList.push({
          parentKey,
          needAddCell: !!column.addCellRender,
          addRowKeys: column.addGroupKey,
        });
        initTreeModel(column.children, parentKey, level + 1);
      } else {
        const nodeIdKey =
          column.mergeRowSpanKey?.[column.mergeRowSpanKey?.length - 1];
        if (nodeIdKey) {
          treeModel.push(
            ...[
              {
                // 树结构id每层唯一
                nodeIdKey: nodeIdKey,
                level: level,
                // 数据索引
                dataIndex: column.dataIndex,
                // 父级id的key
                parentKey: parentKey,
              },
              {
                nodeIdKey: nodeIdKey,
                level: level,
                dataIndex: nodeIdKey,
                parentKey: parentKey,
              },
            ]
          );
        }
      }
    });
  }
  initTreeModel(columns, 'root', 0);
  // 根据dataIndex去重
  treeModel = Array.from(
    new Map(treeModel.map((item) => [item.dataIndex, item])).values()
  );
  treeModel.sort((a, b) => a.level - b.level);
  const levelMap = new Map();
  treeModel.forEach((item) => {
    if (!levelMap.has(item.level)) {
      const needAddCellConfig = needAddCellList.find(
        (addItem) => addItem.parentKey === item.parentKey
      );
      levelMap.set(item.level, {
        level: item.level,
        parentKey: item.parentKey,
        nodeIdKey: item.nodeIdKey,
        needAddCell: needAddCellConfig?.needAddCell ?? false,
        addRowKeys: needAddCellConfig?.addRowKeys ?? [],
      });
    }
  });
  const levelModel: {
    level: number;
    nodeIdKey: string;
    parentKey: string;
    needAddCell: boolean;
    addRowKeys: string[];
  }[] = Array.from(levelMap.values());
  const treeMap = {
    root: {
      childrenId: [],
    },
  };
  // 其他属性放入最末级节点
  data?.forEach?.((item) => {
    // 遍历data,从数据中组成treeMap，如果是parentKey的值
    treeModel?.forEach?.((treeItem, index) => {
      // 将平铺数据转为treeMap
      const rowId = item[treeItem.nodeIdKey];
      if (typeof rowId === 'undefined') return;
      const $parentId =
        treeItem.parentKey === 'root' ? 'root' : item[treeItem.parentKey];

      // 初始化父节点
      if (!treeMap[$parentId]) {
        treeMap[$parentId] = {
          level: treeItem.level,
          childrenId: [],
          ...($parentId !== 'root' ? {} : { $parentId: 'root' }),
        };
      }

      // 添加子节点ID
      if (!treeMap[$parentId].childrenId.includes(rowId)) {
        treeMap[$parentId].childrenId.push(rowId);
      }

      // 初始化当前节点
      if (!treeMap[rowId]) {
        treeMap[rowId] = {
          level: treeItem.level,
          $parentId,
          childrenId: [],
        };
      }

      // 设置数据
      set(treeMap[rowId], treeItem.dataIndex, get(item, treeItem.dataIndex));

      if (item.$addRowParentId === $parentId) {
        set(treeMap[rowId], '$addRow', item.$addRow);
        set(treeMap[rowId], '$addRowParentId', item.$addRowParentId);
      }

      // 处理最后一级节点的其他属性
      if (index === treeModel.length - 1) {
        const otherObj = {};
        Object.keys(item).forEach((n) => {
          if (
            !treeModel.some((item) => item.dataIndex === n) &&
            !AddRowKeys.includes(n)
          ) {
            otherObj[n] = item[n];
          }
        });
        Object.assign(treeMap[rowId], otherObj);
      }
    });
  });
  return {
    treeMap,
    levelModel,
    treeModel,
  };
};

class TableTreeData {
  treeMap: Record<string, any>;
  levelModel: {
    level: number;
    nodeIdKey: string;
    parentKey: string;
    needAddCell: boolean;
    addRowKeys: string[];
  }[];
  treeModel: {
    nodeIdKey: string;
    level: number;
    dataIndex: string | string[];
    parentKey: string;
  }[];
  rowKey: string;
  constructor(
    data: any[],
    columns: AddGroupFormColumn[],
    readOnly = false
  ) {
    const { treeMap, levelModel, treeModel } = getTreeData(data, columns);
    this.treeMap = treeMap;
    this.levelModel = levelModel;
    this.treeModel = treeModel;
    const l0Model = levelModel.find((item) => item.level === 0);
    !readOnly && this.handleAddRowCell(l0Model?.nodeIdKey, 'root');
  }
  renderTableData() {
    let flatData = [];
    // 遍历将数据拍平
    const flat = (key: string, parentData: any, level: number) => {
      const item = this.treeMap[key];
      const { childrenId, ...rest } = item;
      if (!childrenId?.length || childrenId?.[0] == key) {
        // 删除所有l*$index的属性
        Object.keys(rest).forEach((key) => {
          if (key.endsWith('$index')) {
            delete rest[key];
          }
        });
        flatData.push({
          ...parentData,
          ...rest,
        });
      } else {
        childrenId.forEach((childId, index) => {
          flat(
            childId,
            {
              ...parentData,
              ...rest,
              [`l${level}$index`]: index,
            },
            level + 1
          ); // 递归处理
        });
      }
    };
    flat('root', {}, 0);
    return flatData;
  }

  renderTreeData() {
    // 遍历形成树形结构，children对齐childrenId
    const renderTree = (id: string) => {
      const item = this.treeMap[id];
      const { childrenId, ...rest } = item;
      // 如果childrenId为空，则直接添加
      if (!childrenId?.length) {
        return { ...rest };
      } else {
        return { ...rest, children: childrenId.map(renderTree) };
      }
    };
    return renderTree('root');
  }

  set(id: string, values: Record<string, any>) {
    // 深度合并
    const item = this.treeMap[id];
    deepMerge(item, values);
  }
  delete(id) {
    // 解除父子关系，删除
    const item = this.treeMap[id];
    if (!item) return;
    const { childrenId, $parentId, ...rest } = item;
    childrenId.forEach((childId) => {
      delete this.treeMap[childId];
    });
    this.treeMap[$parentId].childrenId = this.treeMap[
      $parentId
    ].childrenId.filter((childId) => childId !== id);
    delete this.treeMap[id];
  }
  copyToParent(nodeIdKey: string | string[], id: string, toParentId: string) {
    const item = this.treeMap[id];
    if (!item) return;
    const { childrenId, $parentId, ...rest } = cloneDeep(item);
    // 包括子节点也要复制
    // 需要基于uuid重置所有的nodeIdKey
    const newId = uuid();
    const newItem = {
      ...rest,
      childrenId: [],
      $parentId: toParentId,
    };
    set(newItem, nodeIdKey, newId);
    this.treeMap[newId] = newItem;
    // 插入节点逻辑
    const children = this.treeMap[toParentId].childrenId;
    // 如果当前节点在toParentId下面, 则追加到当前节点后面一个
    if (children.includes(id)) {
      children.splice(children.indexOf(id) + 1, 0, newId);
    } else {
      // 如果父节点最后一个子节点是addRow，则插入到父节点最后一个子节点后面，否则插入到父节点最后一个子节点前面
      const lastChild = this.treeMap[children[children.length - 1]];
      if (lastChild?.$addRow) {
        children.splice(children.length - 1, 0, newId);
      } else {
        children.push(newId);
      }
    }
    // 插入节点逻辑end

    // 递归处理子节点
    childrenId.forEach((childId) => {
      if (childId == id) return;
      const childRowKey = this.levelModel.find(
        (item) => item.parentKey === nodeIdKey
      )?.nodeIdKey;
      this.copyToParent(childRowKey, childId, newId);
    });
  }

  // 递归处理addRow子节点
  handleAddRowCell(nodeIdKey: string | string[], toParentId: string) {
    // 判断最后一个节点是否有$addRow属性
    const parentNode = this.treeMap[toParentId];

    // 如果当前节点就有addRow，则不添加addRow子节点
    if (parentNode?.$addRow) {
      return;
    }

    const lastNodeId = parentNode.childrenId[parentNode.childrenId.length - 1];
    const lastNode = this.treeMap[lastNodeId];

    // 如果最后一个节点有$addRow属性，则不添加addRow子节点
    if (lastNode?.$addRow) {
      return;
    }

    // 通过nodeIdKey的level判断
    const levelObj = this.levelModel.find(
      (item) => item.nodeIdKey === nodeIdKey
    );
    const level = levelObj?.level;
    // if (level >= this.levelModel.length - 1) return;
    const nextLevel = level + 1;
    const nextNodeIdKey = this.levelModel.find(
      (item) => item.level === nextLevel
    )?.nodeIdKey;

    // 如果父节点有子节点，则需要判断子节点是否是addRow
    if (parentNode.childrenId?.length && nextNodeIdKey) {
      parentNode.childrenId.forEach((childId) => {
        this.handleAddRowCell(nextNodeIdKey, childId);
      });
    }
    // 如果不需要添加cell，则不添加addRow子节点
    if (!levelObj?.needAddCell) {
      return;
    }

    const childAddRowId = uuid();
    const childAddRowItem = {
      childrenId: [],
      $parentId: toParentId,
      $addRow: levelObj.addRowKeys,
      $addRowParentId: toParentId,
    };
    set(childAddRowItem, nodeIdKey, childAddRowId);
    this.treeMap[childAddRowId] = childAddRowItem;
    parentNode.childrenId.push(childAddRowId);

    // 如果添加层级不是最后一级，则需要添加addRow子节点
    if (nextNodeIdKey) {
      this.handleAddRowCell(nextNodeIdKey, childAddRowId);
    }
  }

  // 添加到父节点
  addToParent(
    nodeIdKey: string | string[],
    data: any,
    toParentId: string,
    index: number | 'last' = 'last'
  ) {
    const newId = uuid();
    const levelObj = this.levelModel.find(
      (item) => item.nodeIdKey === nodeIdKey
    );
    const newItem = {
      ...data,
      level: levelObj?.level,
      childrenId: [],
      $parentId: toParentId,
    };
    set(newItem, nodeIdKey, newId);

    this.treeMap[newId] = newItem;

    const children = this.treeMap[toParentId].childrenId;
    if (['last', -1].includes(index)) {
      // 如果父节点最后一个子节点是addRow，则插入到父节点最后一个子节点后面，否则插入到父节点最后一个子节点前面
      const lastChild = this.treeMap[children[children.length - 1]];
      if (lastChild?.$addRow) {
        children.splice(children.length - 1, 0, newId);
      } else {
        children.push(newId);
      }
    } else {
      children.splice(index, 0, newId);
    }

    // 获取nextLevel的nodeIdKey,向下一个level添加addRow
    const nextNodeIdKey = this.levelModel.find(
      (item) => item.parentKey === nodeIdKey
    )?.nodeIdKey;
    if (nextNodeIdKey) {
      this.handleAddRowCell(nextNodeIdKey, newId);
    }
  }
}

export const useAddGroupTableData = (
  data: any[],
  setData: (data: any[]) => void,
  columns: AddGroupFormColumn[],
  readOnly?: boolean
) => {
  const addRowKeys = useMemo(() => {
    // 注意多层addGroup合并,所以需要递归处理
    const addRowKeys = [];
    const getAddGroupKeys = (columns: AddGroupFormColumn[]): string[] => {
      return columns.reduce((keys: string[], item) => {
        if (item.addGroup && item.addGroupKey) {
          keys.push(...item.addGroupKey);
        }
        if (item.children) {
          keys.push(...getAddGroupKeys(item.children));
        }
        return keys;
      }, []);
    };
    addRowKeys.push(...getAddGroupKeys(columns));
    return Array.from(new Set(addRowKeys));
  }, [columns]);

  const [ready, setReady] = useState(false);

  const treeDataRef = useRef<TableTreeData | null>(null);

  const treeInitData = useMemoizedFn((data, columns) => {
    const newTreeData = new TableTreeData(data, columns, readOnly);
    setData(newTreeData?.renderTableData?.());
    treeDataRef.current = newTreeData;
  });

  useEffect(() => {
    // @ts-ignore
    window.$treeTable = treeDataRef;
  }, []);

  useEffect(() => {
    if (addRowKeys.length) {
      treeInitData(data, columns);
      setReady(true);
    }
  }, [addRowKeys]);

  const mergeRowSpan = useMemoizedFn((keys: string[], index: number) => {
    return mergeRowSpanByKeys(keys, index, data);
  });

  // 基于mergeRowSpanKey，格式化column
  const formatRowSpanByMergeKeys = useMemoizedFn(
    (columnP: AddGroupFormColumn) => {
      const { ...column } = columnP;
      if (column.mergeRowSpanKey?.length) {
        const originalOnCell = column.onCell;
        column.onCell = mergeOnCell([
          (record, index) => ({
            rowSpan: mergeRowSpan(column.mergeRowSpanKey, index),
          }),
          originalOnCell,
        ]);
      }
      if (column.children) {
        column.children = column.children.map(formatRowSpanByMergeKeys);
      }
      return column;
    }
  );

  const onValueChangeToTree = useMemoizedFn((event, row, colum, index) => {
    if (colum.mergeRowSpanKey?.length) {
      const nodeIdKey = colum.mergeRowSpanKey[colum.mergeRowSpanKey.length - 1];
      const treeId = get(row, nodeIdKey);
      const value = get(row, colum.dataIndex) ?? '';
      treeSetRowById(treeId, {
        [colum.dataIndex]: value ?? '',
      });
    }
  });

  const addModelColumns = useMemo(() => {
    function formatColumns(
      columns: AddGroupFormColumn[],
      addColumns: AddGroupFormColumn[]
    ) {
      columns?.forEach?.((itemp) => {
        const item = { ...itemp };
        if (item.addGroup) {
          const {
            addCellRender,
            addGroup,
            addGroupKey,
            onCell: parentOnCell,
            title,
            children,
            ...rest
          } = item;
          // 如果addGroup为true，格式化children
          let newChildren = [...children];
          // 第一个column添加addButton,
          // 判断行数据是否有$addRow属性，改写render或editRender方法，同时保留原来的render或editRender方法
          const firstColumnTemp = newChildren[0];
          const { editRender, formItem, render, ...firstColumn } =
            firstColumnTemp;

          if (addCellRender && addGroupKey?.length) {
            firstColumnTemp.render = (v, r, i) => {
              if (
                r.$addRow?.toString() === addGroupKey?.toString() &&
                addCellRender
              ) {
                return addCellRender?.(r, i);
              }
              return render?.(v, r, i);
            };
          } else {
            firstColumnTemp.render = render;
          }

          if (editRender) {
            firstColumnTemp.editRender = (v) => {
              if (
                v.row.$addRow?.toString() === addGroupKey?.toString() &&
                addCellRender
              ) {
                return {
                  name: 'input',
                  editable: false,
                };
              }
              return typeof editRender === 'function'
                ? editRender(v)
                : editRender || { name: 'input', editable: false };
            };
          }

          // SFrom模型支持
          if (formItem) {
            firstColumnTemp.formItem = (v, r, i) => {
              if (
                r.$addRow?.toString() === addGroupKey?.toString() &&
                addCellRender
              ) {
                return {
                  render: () => {
                    return addCellRender?.(r, i);
                  },
                };
              }
              if (typeof formItem === 'function') {
                return formItem?.(v, r, i);
              } else {
                return formItem;
              }
            };
          }

          if (addCellRender && addGroupKey?.length) {
            const getChildrenLength = (
              children: AddGroupFormColumn[]
            ): number => {
              return children.reduce((acc, child) => {
                return (
                  acc + (child.children ? getChildrenLength(child.children) : 1)
                );
              }, 0);
            };
            const addChildrenLength = getChildrenLength(newChildren);
            firstColumnTemp.onCell = mergeOnCell([
              firstColumn.onCell,
              (record, index) => {
                if (!record.$addRow) {
                  return {};
                }

                const level = record.level;
                if (
                  record.$addRow[level]?.toString() ===
                  addGroupKey[level]?.toString()
                ) {
                  return {
                    colSpan: addChildrenLength,
                  };
                }
                return {};
              },
              parentOnCell,
            ]);
            // 其他column的onCell需要根据addGroupKey合并
            newChildren.forEach((child, index) => {
              if (index > 0) {
                child.onCell = mergeOnCell([
                  child.onCell,
                  (record, index) => {
                    if (!record.$addRow) {
                      return {};
                    }
                    const level = record.level;
                    if (
                      record.$addRow[level]?.toString() ===
                      addGroupKey[level]?.toString()
                    ) {
                      return {
                        colSpan: 0,
                      };
                    }
                    return {};
                  },
                  parentOnCell,
                ]);
              }
            });
          }

          // 递归处理children
          newChildren = formatColumns(children, []);

          // 有title则合并添加，否则直接添加
          if (item.title) {
            addColumns.push({
              ...rest,
              title,
              children: newChildren,
            });
          } else {
            addColumns.push(...newChildren);
          }
        } else {
          if (item.children) {
            item.children = formatColumns(item.children, []);
          }
          // 劫持所有受控表单的onChange，基于tree数据维护表单数据
          if (item.editRender && !item.treeChangeReady && addRowKeys) {
            item.treeChangeReady = true;
            const originalEditRender = item.editRender;
            item.editRender = (v) => {
              let renderConfig: any;
              if (typeof originalEditRender === 'function') {
                renderConfig = originalEditRender(v);
              } else {
                renderConfig = originalEditRender;
              }
              // 扩展props.onChange,需要判断原来是否存在onChange，如果没有就新增
              if (!renderConfig.props) {
                renderConfig.props = {};
              }
              const originalOnChange = renderConfig.props.onChange;
              // 基于onValueChangeToTree加强所有onChange，基于tree数据维护表单数据
              renderConfig.props.onChange = (event, row, colum, index) => {
                onValueChangeToTree(event, row, colum, index);
                if (typeof originalOnChange === 'function') {
                  originalOnChange(event, row, colum, index);
                }
              };
              return renderConfig;
            };
          }
          // 如果没有key，但是有dataIndex，则将dataIndex作为key
          if (!item.key && item.dataIndex) {
            item.key = item.dataIndex.toString();
          }
          addColumns.push(formatRowSpanByMergeKeys(item));
        }
      });
      return addColumns;
    }
    const newColumns = formatColumns(columns, []);
    return newColumns;
  }, [columns, addRowKeys]);

  const { run: updateTableData } = useDebounceFn(
    () => {
      if (treeDataRef.current?.renderTableData) {
        setData(treeDataRef.current.renderTableData());
      }
    },
    {
      wait: 400,
    }
  );

  const treeDeleteRowById = useMemoizedFn((id: string) => {
    treeDataRef.current?.delete?.(id);
    updateTableData();
  });

  const treeDeleteRowByIds = useMemoizedFn((ids: string[]) => {
    ids?.forEach((id) => {
      treeDataRef.current?.delete?.(id);
    })
    updateTableData();
  });

  const treeCopyRowById = useMemoizedFn(
    (rowKey: string | string[], id: string, toParentId: string) => {
      treeDataRef.current?.copyToParent?.(rowKey, id, toParentId);
      updateTableData();
    }
  );

  const treeSetRowById = useMemoizedFn(
    (id: string, values: Record<string, any>) => {
      treeDataRef.current?.set?.(id, values);
      updateTableData();
    }
  );

  const treeAddToParent = useMemoizedFn(
    (
      nodeIdKey: string | string[],
      data: any,
      toParentId: string,
      index: number | 'last' = 'last'
    ) => {
      treeDataRef.current?.addToParent?.(nodeIdKey, data, toParentId, index);
      updateTableData();
    }
  );

  return {
    isTree: !!addRowKeys.length,
    treeReady: !!addRowKeys.length ? ready : true,
    columns: addModelColumns,
    mergeRowSpan,
    treeDeleteRowById,
    treeDeleteRowByIds,
    treeCopyRowById,
    treeSetRowById,
    treeAddToParent,
    treeUpdateTableData: updateTableData,
    treeInitData,
  };
};
